<template>
  <purchase-packs-component
    :packs="packs"
    v-model:selected-pack-id="packId"
    :extra="extra"
    v-if="packId"
  ></purchase-packs-component>

  <div class="inner-wrapper">
    <div class="buy-for-section">
      <p class="header-text">{{ $t('rateFinder.buyingFor') }}</p>
      <span class="flag" :class="[country ? `flag:${country.code}` : '']"></span>
      <p class="header-phone-number" @click="changePhoneNumber">+{{ phoneNumber }}</p>
    </div>

    <p class="how-long">
      <span v-if="promoExtra">{{
        $t('buyCredit.howLongWouldXPlusLast', { amount: selectedPack?.formatted, extra: promoExtra })
      }}</span>
      <span v-else>{{ $t('buyCredit.howLongWouldXLast', { amount: selectedPack?.formatted }) }}</span>
    </p>

    <!-- Rate finder -->
    <rate-finder
      :showRateListCard="false"
      :defaultCountry="country.code"
      :defaultCurrency="packs.currency"
      :amount="selectedPack?.amount"
      :extra="extra"
    />

    <div class="promocode-container">
      <promo-code
        @apply="applyPromocode"
        v-model="promoCodeValue"
        v-model:expanded="expandPromocode"
        :applied="!!extra && !promoError"
        :isLoading="isPromocodeLoading"
      />
      <span v-if="promoError" class="promo-error">{{ $t('promocodeError') }}</span>
    </div>
    <div v-if="autoRechargeShow" class="auto-recharge">
      <div class="auto-recharge__head">
        <label>{{ $t('autoRecharge.label') }}</label>
        <ToggleComponent
          :checked="autoRecharge"
          :disabled="!!selectedPack && !selectedPack.allow_recurring"
          @update:checked="changeAutoRecharge"
        />
      </div>
      <p>{{ $t('autoRecharge.subtext') }}</p>
    </div>
    <button class="submit-order" type="button" :disabled="!packId || isLoading" @click="submit">
      {{ $t('buyCredit.buyXNow', { amount: selectedPack?.formatted }) }}
      <span class="spinner" v-if="isLoading">
        <spinner-component />
      </span>
    </button>
    <span v-if="submitError" class="submit-error">{{ $t('submitError') }}</span>
  </div>
</template>

<script lang="ts">
import { ref, computed, Ref, ComputedRef, defineComponent, PropType, onMounted } from 'vue';
import RateFinder from '@/RateFinder.vue';
import PurchasePacksComponent from '@/components/PurchasePacks.vue';
import PromoCode from '@/components/PromoCode.vue';
import ToggleComponent from '@/components/ToggleComponent.vue';
import SpinnerComponent from '@/components/SpinnerComponent.vue';
import { useMixpanel } from '@/composables';
import { submitOrder } from '@/api/products';
import { Country } from '@/models/country';
import { formatCents } from '@/utils/helpers';
import { PurchasePack, PurchasePacks } from '@/types/products';
import { TrackEvents } from '@/types/trackEvents';

export default defineComponent({
  name: 'ShopComponent',
  props: {
    packs: {
      type: Object as PropType<PurchasePacks>,
      required: true,
    },
    country: {
      type: Object as PropType<Country>,
      required: true,
    },
    phoneNumber: {
      type: String,
      required: true,
    },
    promoCode: {
      type: [String, null] as PropType<string | null>,
      required: true,
    },
    extra: {
      type: [Object, null] as PropType<{ amount: number | null; percentage: number | null } | null>,
      required: true,
      default: null,
    },
    isPhonePrefilled: {
      type: Boolean,
      required: true,
    },
    isPromocodeLoading: {
      type: Boolean,
      required: true,
    },
    promoError: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    RateFinder,
    PurchasePacksComponent,
    PromoCode,
    ToggleComponent,
    SpinnerComponent,
  },
  emits: ['showBuyCreditOrRedeemVoucherForm', 'update:extra', 'update:promoCode', 'applyPromocode'],
  setup(props, { emit }) {
    const { track } = useMixpanel();

    const searchParams = new URLSearchParams(location.search);
    const expandPromocode = ref<boolean>(!!searchParams.get('promo_code'));
    const _packId: Ref<number | null> = ref(null);
    const autoRechargeShow = ref(searchParams.get('auto_recharge') ?? false);
    const autoRecharge = ref(searchParams.get('auto_recharge') ? searchParams.get('auto_recharge') === 'true' : false);
    const submitError = ref(false);
    const isLoading = ref(false);

    const formatMoney = formatCents(props.packs?.currency);

    const promoExtra = computed(() => {
      if (!props.extra?.amount) return '';
      return formatMoney(props.extra?.amount);
    });

    const packId: ComputedRef<number | null> = computed({
      get: () => _packId.value ?? props.packs?.products[1]?.id ?? null,
      set: (val: number | null) => {
        _packId.value = val;
      },
    });

    const promoCodeValue = computed({
      get: () => props.promoCode,
      set: (value: string | null) => {
        emit('update:promoCode', value);
      },
    });

    const selectedPack: ComputedRef<PurchasePack | null> = computed(() => {
      const pack = props.packs?.products.find((product) => product.id === packId.value);

      const result = pack ? { ...pack, formatted: formatMoney(pack.amount) } : null;
      return result;
    });

    onMounted(() => {
      track(TrackEvents.BUY_CREDIT_VIEW);
    });

    const applyPromocode = async (code: string) => {
      emit('applyPromocode', code);
    };

    const changeAutoRecharge = (value: boolean) => {
      autoRecharge.value = value;
    };

    const changePhoneNumber = () => {
      if (props.isPhonePrefilled) return;
      emit('showBuyCreditOrRedeemVoucherForm');
    };

    const submit = async () => {
      if (packId.value === null) {
        throw new Error("Can't submit without packId");
      }
      if (props.packs === null) {
        throw new Error("Can't submit without purchasePacks");
      }

      track(TrackEvents.PAYMENT_FLOW_STARTED, {
        package_id: packId.value,
        currency: props.packs.currency,
      });

      try {
        isLoading.value = true;
        const url = await submitOrder(
          props.packs.currency,
          props.country,
          props.phoneNumber,
          props.promoCode,
          packId.value,
          autoRecharge.value,
          document.documentElement.getAttribute('lang') ?? 'en',
        );
        window.location.href = url;
      } catch (e) {
        submitError.value = true;
        isLoading.value = false;
      }
    };

    return {
      autoRecharge,
      autoRechargeShow,
      packId,
      selectedPack,
      expandPromocode,
      promoExtra,
      promoCodeValue,
      submitError,
      isLoading,
      applyPromocode,
      changeAutoRecharge,
      changePhoneNumber,
      submit,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../flags';

.buy-for-section {
  display: flex;
  align-items: center;
  margin-top: 1.5rem !important;
}

.inner-wrapper {
  padding: 0 0.875rem;
}

.header-text {
  @extend %label;
  margin-bottom: 0;
}

.flag {
  display: inline-block;
  margin-left: auto;
  margin-right: 0.25rem;
  font-size: 1rem;
}

.header-phone-number {
  color: $primary-color;
  font-size: 0.875rem;
  cursor: pointer;
}

.how-long {
  margin: 12px 0;
  * {
    @extend %label;
  }
}

.submit-order {
  position: relative;
  background: lighten($color: $color-primary, $amount: 10);

  &:disabled {
    background: lighten($color: $color-primary, $amount: 10) !important;
  }

  .spinner {
    position: absolute !important;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
